import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ACCEPTED_TYPES,
  ACCEPTED_TYPES_LABEL,
  ACCEPT_ATTR,
  MAX_DIMENSION,
  LARGE_FILE_THRESHOLD,
  QUALITY_HIGH,
  QUALITY_MEDIUM,
  QUALITY_LOW,
  MSG_UNSUPPORTED_FORMAT,
  MSG_UNREADABLE,
  MSG_OPTIMIZATION_FAILED,
  SHOW_DEV_CONTROLS,
  FREE_UPLOAD_LIMIT,
  FREE_POSTING_LIMIT,
  PLANS,
  STATUS_LABELS,
  statusClassFor,
  createMockFile,
} from "../Constants/ImageOptimizer";
import "../Styles/ImageOptimizerStyle.css";

/* ============================================================
   IMAGE OPTIMIZER
   Frontend-only image upload + automatic optimization page.
   Constants live in Constants/ImageOptimizer.js, styling lives
   in Styles/ImageOptimizerStyle.css (all classes prefixed
   "Optimizer"). This file contains logic + UI components only.
   ============================================================ */

/* ------------------------------------------------------------
   FILE VALIDATION
   ------------------------------------------------------------ */
function validateFile(file) {
  if (!file) return MSG_UNREADABLE;
  if (!ACCEPTED_TYPES.includes(file.type)) return MSG_UNSUPPORTED_FORMAT;
  if (file.size === 0) return MSG_UNREADABLE;
  return null;
}

/* ------------------------------------------------------------
   FILE SIZE FORMATTER
   ------------------------------------------------------------ */
function formatFileSize(bytes) {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes)) return "—";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`; 
  const mb = kb / 1024;
  return `${mb.toFixed(1)} MB`;
}

/* ------------------------------------------------------------
   DIMENSION CALCULATIONS
   ------------------------------------------------------------ */
function getTargetDimensions(width, height, maxDimension) {
  const largestSide = Math.max(width, height);
  if (largestSide <= maxDimension) {
    return { width, height };
  }
  const scale = maxDimension / largestSide;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/* ------------------------------------------------------------
   IMAGE OPTIMIZATION FUNCTIONS
   ------------------------------------------------------------ */
function loadImageElement(file) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve({ img, objectUrl });
    img.onerror = (event) => {
      URL.revokeObjectURL(objectUrl);
      console.error("[ImageOptimizer] image failed to decode:", event);
      reject(new Error("DECODE_ERROR"));
    };
    img.src = objectUrl;
  });
}

function canvasToBlob(canvas, mimeType, quality) {
  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob((blob) => resolve(blob), mimeType, quality);
    } catch (err) {
      // Most commonly a SecurityError ("tainted canvas"), which happens
      // when the page is opened directly from disk (file://) instead of
      // through a dev server, or when a strict CSP blocks blob: sources.
      reject(err);
    }
  });
}

async function optimizeImage(file) {
  const { img, objectUrl } = await loadImageElement(file);

  const originalWidth = img.naturalWidth;
  const originalHeight = img.naturalHeight;
  const originalSize = file.size;

  if (!originalWidth || !originalHeight) {
    URL.revokeObjectURL(objectUrl);
    throw new Error("DECODE_ERROR");
  }

  const { width: targetWidth, height: targetHeight } = getTargetDimensions(
    originalWidth,
    originalHeight,
    MAX_DIMENSION
  );

  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
  URL.revokeObjectURL(objectUrl);

  const wasResized = targetWidth !== originalWidth || targetHeight !== originalHeight;
  let quality = QUALITY_HIGH;
  if (wasResized) {
    quality = QUALITY_LOW;
  } else if (originalSize > LARGE_FILE_THRESHOLD) {
    quality = QUALITY_MEDIUM;
  }

  let blob = await canvasToBlob(canvas, "image/webp", quality);
  let outputType = "image/webp";
  if (!blob || blob.type !== "image/webp") {
    blob = await canvasToBlob(canvas, "image/jpeg", quality);
    outputType = "image/jpeg";
  }
  if (!blob) {
    throw new Error("OPTIMIZATION_FAILED");
  }

  const extension = outputType === "image/webp" ? "webp" : "jpg";
  const baseName = file.name.replace(/\.[^/.]+$/, "") || "image";
  const optimizedFile = new File([blob], `${baseName}-optimized.${extension}`, {
    type: outputType,
  });

  const previewUrl = URL.createObjectURL(blob);
  const optimizedSize = optimizedFile.size;
  const reductionPercent =
    optimizedSize < originalSize
      ? Math.round(((originalSize - optimizedSize) / originalSize) * 100)
      : null;

  return {
    optimizedFile,
    originalSize,
    optimizedSize,
    reductionPercent,
    originalWidth,
    originalHeight,
    optimizedWidth: targetWidth,
    optimizedHeight: targetHeight,
    previewUrl,
  };
}

/* ------------------------------------------------------------
   ID GENERATOR
   ------------------------------------------------------------ */
let idCounter = 0;
function createId() {
  idCounter += 1;
  return `io-${Date.now()}-${idCounter}`;
}

function createEmptyEntry(file) {
  return {
    id: createId(),
    name: file.name,
    status: "uploading",
    error: null,
    originalFile: file,
    optimizedFile: null,
    previewUrl: null,
    originalSize: file.size,
    optimizedSize: null,
    reductionPercent: null,
    originalWidth: null,
    originalHeight: null,
    optimizedWidth: null,
    optimizedHeight: null,
  };
}

/* ============================================================
   MAIN COMPONENT
   ============================================================ */
export default function ImageOptimizer() {
  const [images, setImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [replaceTargetId, setReplaceTargetId] = useState(null);
  const [activePlan, setActivePlan] = useState(null); // null = free plan
  const [limitOverlayOpen, setLimitOverlayOpen] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [postOverlayOpen, setPostOverlayOpen] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [uploadLimitOverlayOpen, setUploadLimitOverlayOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const currentLimit = activePlan ? activePlan.uploadLimit : FREE_UPLOAD_LIMIT;
  const currentPostingLimit = activePlan ? activePlan.postingLimit : FREE_POSTING_LIMIT;
  const currentPlanName = activePlan ? activePlan.name : "Free Plan";

  const fileInputRef = useRef(null);
  const replaceInputRef = useRef(null);
  const uploadAreaRef = useRef(null);
  const gridRef = useRef(null);
  const dragCounterRef = useRef(0);
  const imagesRef = useRef(images);

  useEffect(() => {
    imagesRef.current = images;
  }, [images]);

  // Revoke every remaining preview URL on unmount to avoid memory leaks.
  useEffect(() => {
    return () => {
      imagesRef.current.forEach((entry) => {
        if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
      });
    };
  }, []);

  const updateImage = useCallback((id, patch) => {
    setImages((prev) =>
      prev.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry))
    );
  }, []);

  const processFile = useCallback(
    async (id, file) => {
      const validationError = validateFile(file);
      if (validationError) {
        updateImage(id, { status: "error", error: validationError });
        return;
      }
      updateImage(id, { status: "optimizing", error: null });
      try {
        const result = await optimizeImage(file);
        updateImage(id, {
          status: "ready",
          error: null,
          optimizedFile: result.optimizedFile,
          previewUrl: result.previewUrl,
          optimizedSize: result.optimizedSize,
          reductionPercent: result.reductionPercent,
          originalWidth: result.originalWidth,
          originalHeight: result.originalHeight,
          optimizedWidth: result.optimizedWidth,
          optimizedHeight: result.optimizedHeight,
        });
      } catch (err) {
        // Log the real error for debugging — the UI intentionally shows a
        // simple, non-technical message, but this tells you the actual cause
        // (decode failure, tainted canvas, unsupported toBlob, etc.) in the
        // browser console.
        console.error("[ImageOptimizer] optimization failed:", err);
        updateImage(id, { status: "error", error: MSG_OPTIMIZATION_FAILED });
      }
    },
    [updateImage]
  );

  const handleFiles = useCallback(
    (fileList) => {
      const files = Array.from(fileList || []);
      if (files.length === 0) return;

      const totalAfterUpload = images.length + files.length;
      if (totalAfterUpload > 20) {
        setUploadLimitOverlayOpen(true);
        return;
      }

      files.forEach((file) => {
        const entry = createEmptyEntry(file);
        setImages((prev) => [...prev, entry]);
        processFile(entry.id, file);
      });

      setTimeout(() => {
        gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        setTimeout(() => {
          window.scrollBy({ top: 400, behavior: "smooth" });
        }, 350);
      }, 300);
    },
    [processFile, images.length]
  );

  const handleChoosePlan = useCallback(
    (plan) => {
      setActivePlan(plan);
      setLimitOverlayOpen(false);
      const files = pendingFiles;
      setPendingFiles([]);
      files.forEach((file) => {
        const entry = createEmptyEntry(file);
        setImages((prev) => [...prev, entry]);
        processFile(entry.id, file);
      });
    },
    [pendingFiles, processFile]
  );

  const closeLimitOverlay = useCallback(() => {
    setLimitOverlayOpen(false);
    setPendingFiles([]);
  }, []);

  const openFileDialog = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const onFileInputChange = useCallback(
    (e) => {
      handleFiles(e.target.files);
      e.target.value = "";
    },
    [handleFiles]
  );

  const onDragEnter = useCallback((e) => {
    e.preventDefault();
    dragCounterRef.current += 1;
    setIsDragging(true);
  }, []);

  const onDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const onDragLeave = useCallback((e) => {
    e.preventDefault();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDragging(false);
    }
  }, []);

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      dragCounterRef.current = 0;
      setIsDragging(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  const handleUploadAreaKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openFileDialog();
      }
    },
    [openFileDialog]
  );

  const handleReplaceClick = useCallback((id) => {
    setReplaceTargetId(id);
    requestAnimationFrame(() => replaceInputRef.current?.click());
  }, []);

  const handleReplaceChange = useCallback(
    (e) => {
      const file = e.target.files && e.target.files[0];
      e.target.value = "";
      if (!file || !replaceTargetId) return;
      const id = replaceTargetId;
      setReplaceTargetId(null);

      setImages((prev) =>
        prev.map((entry) => {
          if (entry.id !== id) return entry;
          if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
          return {
            ...createEmptyEntry(file),
            id, // keep the same card identity
          };
        })
      );
      processFile(id, file);
    },
    [replaceTargetId, processFile]
  );

  const handleRemove = useCallback((id) => {
    setImages((prev) => {
      const target = prev.find((entry) => entry.id === id);
      if (target && target.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((entry) => entry.id !== id);
    });
  }, []);

  const handlePost = useCallback(
    (id) => {
      updateImage(id, { status: "posted" });
      setOverlayOpen(true);
    },
    [updateImage]
  );

  const closeOverlay = useCallback(() => setOverlayOpen(false), []);

  // Development-only helpers (hidden unless SHOW_DEV_CONTROLS is true).
  const addMockSmall = useCallback(async () => {
    const file = await createMockFile(480, 360, "Mock Small");
    handleFiles([file]);
  }, [handleFiles]);

  const addMockLarge = useCallback(async () => {
    const file = await createMockFile(4000, 3000, "Mock Large");
    handleFiles([file]);
  }, [handleFiles]);

  const clearImages = useCallback(() => {
    imagesRef.current.forEach((entry) => {
      if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
    });
    setImages([]);
  }, []);

  const continueUploading = useCallback(() => {
    setOverlayOpen(false);
    clearImages();
    uploadAreaRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [clearImages]);

  const resetAll = useCallback(() => {
    clearImages();
    setOverlayOpen(false);
  }, [clearImages]);

  const closePostOverlay = useCallback(() => {
    setPostOverlayOpen(false);
    setSelectedImages([]);
  }, []);

  const toggleImageSelection = useCallback((id) => {
    setSelectedImages((prev) => {
      if (prev.includes(id)) {
        return prev.filter((i) => i !== id);
      }
      if (prev.length >= currentPostingLimit) {
        setLimitOverlayOpen(true);
        return prev;
      }
      return [...prev, id];
    });
  }, [currentPostingLimit]);

  const readyImages = images.filter(
    (entry) => entry.status === "ready" || entry.status === "posted"
  );
  const totalOriginal = readyImages.reduce(
    (sum, entry) => sum + (entry.originalSize || 0),
    0
  );
  const totalOptimized = readyImages.reduce(
    (sum, entry) => sum + (entry.optimizedSize || 0),
    0
  );
  const totalSaved = totalOriginal - totalOptimized;
  const overallReduction =
    totalOriginal > 0 && totalSaved > 0
      ? Math.round((totalSaved / totalOriginal) * 100)
      : null;

  const openPostOverlay = useCallback(() => {
    const autoSelected = readyImages.slice(0, currentPostingLimit).map((img) => img.id);
    setSelectedImages(autoSelected);
    setPostOverlayOpen(true);
  }, [readyImages, currentPostingLimit]);

  const selectAllImages = useCallback(() => {
    setSelectedImages(readyImages.map((img) => img.id));
  }, [readyImages]);

  const postSelectedImages = useCallback(() => {
    const idsToPost = selectedImages.length > 0
      ? selectedImages.slice(0, currentPostingLimit)
      : readyImages.slice(0, currentPostingLimit).map((img) => img.id);
    idsToPost.forEach((id) => updateImage(id, { status: "posted" }));
    setPostOverlayOpen(false);
    setSelectedImages([]);
    setOverlayOpen(true);
  }, [selectedImages, readyImages, updateImage, currentPostingLimit]);

  return (
    <div className="OptimizerPage">
      <div className="OptimizerContainer">
        <header className="OptimizerHeader">
          <h1 className="OptimizerHeading">Upload Your Images</h1>
          <p className="OptimizerSubtext">
            Upload your images and we will automatically optimize them for
            profile use.
          </p>
          <p className="OptimizerUsageText">
            {currentPostingLimit} of {images.length} uploads
            used for Posting - {currentPlanName}
          </p>
        </header>

        <section
          ref={uploadAreaRef}
          className={`OptimizerUploadArea${isDragging ? " OptimizerUploadAreaDragging" : ""}`}
          onDragEnter={onDragEnter}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          role="button"
          tabIndex={0}
          aria-label="Upload images by dragging and dropping, or choosing files from your device"
          onClick={openFileDialog}
          onKeyDown={handleUploadAreaKeyDown}
        >
          <p className="OptimizerUploadTitle">Drag &amp; drop your images here</p>
          <p className="OptimizerUploadOr">or</p>
          <button
            type="button"
            className="OptimizerBtn OptimizerBtnPrimary"
            onClick={(e) => {
              e.stopPropagation();
              openFileDialog();
            }}
          >
            Choose Files
          </button>
          <p className="OptimizerUploadSupport">Supported: {ACCEPTED_TYPES_LABEL}</p>

          <label htmlFor="optimizer-file-input" className="OptimizerSrOnly">
            Choose images to upload
          </label>
          <input
            ref={fileInputRef}
            id="optimizer-file-input"
            className="OptimizerSrOnly"
            type="file"
            accept={ACCEPT_ATTR}
            multiple
            onChange={onFileInputChange}
          />
        </section>

        <label htmlFor="optimizer-replace-input" className="OptimizerSrOnly">
          Choose a replacement image
        </label>
        <input
          ref={replaceInputRef}
          id="optimizer-replace-input"
          className="OptimizerSrOnly"
          type="file"
          accept={ACCEPT_ATTR}
          onChange={handleReplaceChange}
          tabIndex={-1}
        />

        {images.length > 0 && (
          <section ref={gridRef} className="OptimizerGrid" aria-label="Uploaded images">
            {images.map((entry) => (
              <ImageCard
                key={entry.id}
                image={entry}
                onReplace={() => handleReplaceClick(entry.id)}
                onRemove={() => handleRemove(entry.id)}
                onPreview={() => setPreviewImage(entry)}
              />
            ))}
          </section>
        )}

        {readyImages.length > 0 && (
          <div className="OptimizerPostActions">
            <button
              type="button"
              className="OptimizerBtn OptimizerBtnPrimary OptimizerBtnFull"
              onClick={openPostOverlay}
            >
              Post to Profile
            </button>
          </div>
        )}

        {readyImages.length > 0 && (
          <section className="OptimizerSummary" aria-label="Upload summary">
            <h2 className="OptimizerSectionHeading">Upload Summary</h2>
            <div className="OptimizerSummaryGrid">
              <SummaryStat label="Original Total" value={formatFileSize(totalOriginal)} />
              <SummaryStat label="Optimized Total" value={formatFileSize(totalOptimized)} />
              <SummaryStat
                label="Total Saved"
                value={formatFileSize(Math.max(totalSaved, 0))}
              />
              <SummaryStat
                label="Overall Reduction"
                value={overallReduction !== null ? `${overallReduction}%` : "No Size Reduction"}
              />
            </div>
          </section>
        )}

        {SHOW_DEV_CONTROLS && (
          <section className="OptimizerDev" aria-label="Development controls">
            <p className="OptimizerDevLabel">Development Controls</p>
            <div className="OptimizerDevButtons">
              <button type="button" className="OptimizerBtn OptimizerBtnSecondary" onClick={addMockSmall}>
                Add Mock Small Image
              </button>
              <button type="button" className="OptimizerBtn OptimizerBtnSecondary" onClick={addMockLarge}>
                Add Mock Large Image
              </button>
              <button type="button" className="OptimizerBtn OptimizerBtnSecondary" onClick={clearImages}>
                Clear Images
              </button>
              <button type="button" className="OptimizerBtn OptimizerBtnSecondary" onClick={resetAll}>
                Reset All
              </button>
            </div>
          </section>
        )}
      </div>

      {overlayOpen && (
        <div className="OptimizerOverlayBackdrop" role="presentation">
          <div
            className="OptimizerOverlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="optimizer-overlay-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="optimizer-overlay-title" className="OptimizerOverlayTitle">
              Image Posted
            </h2>
            <p className="OptimizerOverlayText">
              Your image has been successfully posted to your profile.
            </p>
            <div className="OptimizerOverlayActions">
              <button
                type="button"
                className="OptimizerBtn OptimizerBtnPrimary"
                onClick={continueUploading}
                autoFocus
              >
                Continue Uploading
              </button>
            </div>
          </div>
        </div>
      )}

      {limitOverlayOpen && (
        <div className="OptimizerOverlayBackdrop OptimizerOverlayHigh" role="presentation" onClick={closeLimitOverlay}>
          <div
            className="OptimizerOverlay OptimizerOverlayWide"
            role="dialog"
            aria-modal="true"
            aria-labelledby="optimizer-limit-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="OptimizerOverlayClose"
              onClick={closeLimitOverlay}
              aria-label="Close"
            >
              ✕
            </button>
            <h2 id="optimizer-limit-title" className="OptimizerOverlayTitle">
              Posting Limit Reached
            </h2>
            <p className="OptimizerOverlayText">
              You've used {selectedImages.length} of {currentPostingLimit} Posting limit on your{" "}
              {currentPlanName}. Choose a plan to keep Posting.
            </p>

            <div className="OptimizerPlanList">
              {PLANS.map((plan) => (
                <div key={plan.id} className="OptimizerPlanCard">
                  <p className="OptimizerPlanName">{plan.name}</p>
                  <p className="OptimizerPlanDetail">{plan.postingLimit} postings</p>
                  <p className="OptimizerPlanPrice">{plan.price}</p>
                  <button
                    type="button"
                    className="OptimizerBtn OptimizerBtnPrimary OptimizerBtnFull"
                    onClick={() => handleChoosePlan(plan)}
                  >
                    Choose Plan
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {postOverlayOpen && (
        <div className="OptimizerOverlayBackdrop" role="presentation">
          <div
            className="OptimizerOverlay OptimizerOverlayWide"
            role="dialog"
            aria-modal="true"
            aria-labelledby="optimizer-post-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="OptimizerOverlayClose"
              onClick={closePostOverlay}
              aria-label="Close"
            >
              ✕
            </button>
            <h2 id="optimizer-post-title" className="OptimizerOverlayTitle">
              Post to Profile
            </h2>
            <p className="OptimizerOverlayText">
              You have {readyImages.length} optimized image{readyImages.length !== 1 ? "s" : ""} ready to post. Select images to post.
            </p>

            <div className="OptimizerPostList">
              {readyImages.map((img, index) => (
                <div
                  key={img.id}
                  className={"OptimizerPostListItem" + (selectedImages.includes(img.id) ? " Selected" : "")}
                >
                  <img
                    src={img.previewUrl}
                    alt={img.name}
                    className="OptimizerPostListThumb"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewImage(img);
                    }}
                  />
                  <span className="OptimizerPostListItemLabel">Image {index + 1}</span>
                  <div className="OptimizerPostListActions">
                    <button
                      type="button"
                      className="OptimizerPostListDownload"
                      onClick={(e) => {
                        e.stopPropagation();
                        const a = document.createElement("a");
                        a.href = img.previewUrl;
                        a.download = `optimized-${img.name}`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                      }}
                      aria-label="Download image"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                    </button>
                    <span
                      className={"OptimizerPostListCheck" + (selectedImages.includes(img.id) ? " Visible" : "")}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleImageSelection(img.id);
                      }}
                    >✓</span>
                  </div>
                </div>
              ))}
            </div>

            <button
              type="button"
              className="OptimizerBtn OptimizerBtnPrimary OptimizerBtnFull"
              onClick={postSelectedImages}
              disabled={selectedImages.length === 0}
            >
              Submit
            </button>
          </div>
        </div>
      )}

      {uploadLimitOverlayOpen && (
        <div className="OptimizerOverlayBackdrop OptimizerOverlayHigh" role="presentation">
          <div
            className="OptimizerOverlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="optimizer-upload-limit-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="OptimizerOverlayClose"
              onClick={() => setUploadLimitOverlayOpen(false)}
              aria-label="Close"
            >
              ✕
            </button>
            <h2 id="optimizer-upload-limit-title" className="OptimizerOverlayTitle">
              Upload Limit
            </h2>
            <p className="OptimizerOverlayText">
              You can upload up to 20 images at a time. Please Try again.
            </p>
          </div>
        </div>
      )}

      {previewImage && (
        <div className="OptimizerPreviewBackdrop" role="presentation" onClick={() => setPreviewImage(null)}>
          <button
            type="button"
            className="OptimizerPreviewDownload"
            onClick={(e) => {
              e.stopPropagation();
              const a = document.createElement("a");
              a.href = previewImage.previewUrl;
              a.download = `optimized-${previewImage.name}`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
            }}
            aria-label="Download image"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </button>
          <button
            type="button"
            className="OptimizerOverlayClose"
            onClick={(e) => {
              e.stopPropagation();
              setPreviewImage(null);
            }}
            aria-label="Close"
          >
            ✕
          </button>
          <img
            src={previewImage.previewUrl}
            alt={previewImage.name}
            className="OptimizerPreviewImg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------
   UI COMPONENTS
   ------------------------------------------------------------ */
function ImageCard({ image, onReplace, onRemove, onPreview }) {
  const isBusy = image.status === "uploading" || image.status === "optimizing";
  const isReadyForInfo = image.status === "ready" || image.status === "posted";
  const statusLabel = STATUS_LABELS[image.status] || "";
  const [summaryOpen, setSummaryOpen] = useState(false);

  const handleDownload = useCallback(async () => {
    if (!image.previewUrl) return;
    try {
      const response = await fetch(image.previewUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `optimized-${image.name}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
    }
  }, [image.previewUrl, image.name]);

  return (
    <article className="OptimizerCard">
      <button
        type="button"
        className="OptimizerCardClose"
        onClick={onRemove}
        aria-label="Remove image"
      >
        ✕
      </button>
      <button
        type="button"
        className="OptimizerCardDownload"
        onClick={handleDownload}
        aria-label="Download optimized image"
        disabled={!image.previewUrl || isBusy}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
      </button>
      <div className="OptimizerCardPreview" onClick={onPreview} style={{ cursor: image.previewUrl ? "pointer" : "default" }}>
        {image.previewUrl ? (
          <img
            src={image.previewUrl}
            alt={`Optimized preview of ${image.name}`}
            className="OptimizerCardImg"
          />
        ) : (
          <div className="OptimizerCardPlaceholder">
            {isBusy ? (
              <span className="OptimizerSpinner" aria-hidden="true" />
            ) : (
              <span className="OptimizerPlaceholderText">No preview available</span>
            )}
          </div>
        )}
      </div>

      <p className={"OptimizerStatus " + statusClassFor(image.status)} role="status">
        {statusLabel}
      </p>

      {image.status === "error" && (
        <p className="OptimizerErrorText">{image.error}</p>
      )}

      {isReadyForInfo && (
        <div className="OptimizerCardInfo">
          <button
            type="button"
            className="OptimizerInfoToggle"
            onClick={() => setSummaryOpen((o) => !o)}
          >
            <span className="OptimizerInfoHeading">Optimization Summary</span>
            <svg className={"OptimizerInfoArrow" + (summaryOpen ? " Open" : "")} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          <div className={"OptimizerInfoBody" + (summaryOpen ? " Open" : "")}>
            <InfoRow label="Original Size" value={formatFileSize(image.originalSize)} />
            <InfoRow label="Optimized Size" value={formatFileSize(image.optimizedSize)} />
            <InfoRow
              label="Reduced"
              value={
                image.reductionPercent !== null
                  ? `${image.reductionPercent}%`
                  : "No Size Reduction"
              }
            />
            <InfoRow
              label="Original Dimensions"
              value={`${image.originalWidth} × ${image.originalHeight}`}
            />
            <InfoRow
              label="Optimized Dimensions"
              value={`${image.optimizedWidth} × ${image.optimizedHeight}`}
            />
          </div>
        </div>
      )}
    </article>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="OptimizerInfoRow">
      <span className="OptimizerInfoLabel">{label}</span>
      <span className="OptimizerInfoValue">{value}</span>
    </div>
  );
}

function SummaryStat({ label, value }) {
  return (
    <div className="OptimizerSummaryStat">
      <span className="OptimizerSummaryValue">{value}</span>
      <span className="OptimizerSummaryLabel">{label}</span>
    </div>
  );
}
